import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useAuth } from '@/lib/contexts/AuthContext'
import { archiveProperty } from '@/lib/api/api'
import { Input } from '@/components/ui/input'
import { HugeiconsIcon } from '@hugeicons/react'
import { Loading01Icon } from '@hugeicons/core-free-icons'
import {
  SettingsWell,
  SettingsCard,
  SettingsRow,
  SettingsField,
  SettingsDangerAction,
  settingsButtonClass,
  settingsDangerOutlineClass,
} from '@/components/shared/settings/SettingsKit'

interface DangerZoneTabProps {
  domain: string
  propertyId?: string
}

export default function DangerZoneTab({ domain, propertyId }: DangerZoneTabProps) {
  const { token } = useAuth()
  const [confirming, setConfirming] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  // A site whose domain hasn't loaded would otherwise match an empty field.
  const canDelete = domain.trim() !== '' && confirmText.trim() === domain

  const handleDeleteSite = async () => {
    if (!token || !propertyId) {
      toast.error('Unable to delete site')
      return
    }

    if (!canDelete) return

    setIsDeleting(true)
    try {
      await archiveProperty(propertyId, token)
      toast.success('Site deleted')
      window.location.href = '/'
    } catch (error) {
      console.error('Error deleting site:', error)
      toast.error("Couldn't delete the site")
    } finally {
      setIsDeleting(false)
    }
  }

  const cancel = () => {
    setConfirming(false)
    setConfirmText('')
  }

  return (
    <SettingsWell>
      <SettingsCard danger>
        <SettingsRow title="Danger zone" description="Irreversible actions for this site">
          <SettingsDangerAction
            title={`Delete ${domain || 'this site'}`}
            description="Archives the site and hides it from your dashboards. Historical data is kept, other sites are unaffected, and support can restore it."
            action={
              !confirming && (
                <Button variant="outline" onClick={() => setConfirming(true)} className={settingsDangerOutlineClass}>
                  Delete site
                </Button>
              )
            }
          >
            {confirming && (
              <>
                <SettingsField label={`Type ${domain} to confirm`} htmlFor="confirm-delete-site">
                  <Input
                    id="confirm-delete-site"
                    autoFocus
                    autoComplete="off"
                    placeholder={domain}
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    className="h-9 rounded-lg text-sm"
                  />
                </SettingsField>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={cancel} disabled={isDeleting} className={settingsButtonClass}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteSite}
                    disabled={!canDelete || isDeleting}
                    className={`${settingsButtonClass} gap-1.5 bg-danger text-white hover:bg-danger/90`}
                  >
                    {isDeleting && <HugeiconsIcon icon={Loading01Icon} size={14} className="animate-spin" />}
                    Delete site
                  </Button>
                </div>
              </>
            )}
          </SettingsDangerAction>
        </SettingsRow>
      </SettingsCard>
    </SettingsWell>
  )
}
